import { DateTime } from 'luxon';
import * as interaction from 'constants-interactions';
import tokens from '@viewthespace/style-dictionary/assets/json/tokens.json';
import { ReservableBooking } from 'graphql-query-contracts';
import { reservableBookingColorMapper } from 'domains/reservableManagement/utilities/reservableBookingColorMapper';
import { getEarliestJoinedCompanyOrEmptyString } from 'domains/reservableManagement/utilities/getEarliestJoinedCompanyOrEmptyString';
import { getUniqueTimeBookings } from 'domains/reservableManagement/utilities/getUniqueTimeBookings';
import { FullCalendarEvent } from '../hooks/useReservableCalendarView';

export const MONTH_EVENT_TRANSLATION_KEYS = {
  multipleEntriesTitle: 'web.admin.channel.reservableCalendar.multipleEntries',
};

type MonthReservable = {
  id: string;
  name: string;
  timeZone?: string | null;
  features?: unknown;
  tags?: Array<string | null> | null;
  reservableBookings: ReservableBooking[];
};

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

type LabelVariant = 'compact' | 'full';

type MapOptions = {
  channelTimeZone: string;
  locale: string;
  t: TranslateFn;
};

// Always formatted in the booking's own reservable timezone, not the channel tz.
export function formatMonthEventTime(
  start: Date,
  timeZone: string,
  locale: string,
  variant: LabelVariant
): string {
  const dateTime = DateTime.fromJSDate(start)
    .setZone(timeZone)
    .setLocale(locale);
  const hour12 = dateTime.hour % 12 === 0 ? 12 : dateTime.hour % 12;
  const minutes = dateTime.minute.toString().padStart(2, '0');

  if (variant === 'full') {
    const meridiem = dateTime.hour < 12 ? 'AM' : 'PM';

    return `${hour12}:${minutes} ${meridiem}`;
  }

  const meridiem = dateTime.hour < 12 ? 'a' : 'p';

  return `${hour12}:${minutes}${meridiem}`;
}

export function formatMonthEventLabel({
  start,
  reservableName,
  detail,
  timeZone,
  locale,
  variant,
}: {
  start: Date;
  reservableName: string;
  detail: string;
  timeZone: string;
  locale: string;
  variant: LabelVariant;
}): string {
  const time = formatMonthEventTime(start, timeZone, locale, variant);

  return `${time} ${reservableName} (${detail})`;
}

// Day membership is bucketed in the channel timezone.
export function getEventsForDay(
  events: FullCalendarEvent[],
  date: Date,
  channelTimeZone: string
): FullCalendarEvent[] {
  const target = DateTime.fromJSDate(date).setZone(channelTimeZone);

  return events.filter(event =>
    DateTime.fromJSDate(event.start)
      .setZone(channelTimeZone)
      .hasSame(target, 'day')
  );
}

function resolveBookingStatus(statuses: string[]): string {
  if (statuses.length > 1) {
    return new Set(statuses).size > 1
      ? interaction.INTERACTION_CREATED
      : statuses[0];
  }

  return statuses[0];
}

// Bookings-only: unlike the resource-day view, no resources/businessHours are built.
export function mapReservablesToMonthEvents(
  reservables: MonthReservable[],
  { channelTimeZone, locale, t }: MapOptions
): FullCalendarEvent[] {
  const events: FullCalendarEvent[] = [];

  reservables.forEach(reservable => {
    if (!reservable.features) {
      return;
    }

    const timeZone = reservable.timeZone ?? channelTimeZone;
    const uniqueTimeBookings = getUniqueTimeBookings(
      reservable.reservableBookings
    );

    uniqueTimeBookings.forEach(booking => {
      const start = new Date(booking.startDate);
      const end = new Date(booking.endDate);

      const differenceInMinutes = Math.abs(
        DateTime.fromISO(booking.startDate).diff(
          DateTime.fromISO(booking.endDate),
          'minutes'
        ).minutes
      );

      const companyName = getEarliestJoinedCompanyOrEmptyString(
        booking.companies
      );

      const detail =
        booking.count > 1
          ? t(MONTH_EVENT_TRANSLATION_KEYS.multipleEntriesTitle, {
              bookingCount: booking.count,
            })
          : booking.username;

      const title = formatMonthEventLabel({
        start,
        reservableName: reservable.name,
        detail,
        timeZone,
        locale,
        variant: 'compact',
      });

      const bookingStatus = resolveBookingStatus(booking.status);
      const { background, border } =
        reservableBookingColorMapper(bookingStatus);

      events.push({
        title,
        start,
        end,
        resourceId: reservable.id,
        backgroundColor: background,
        borderColor: border,
        textColor: tokens.color.text.primary,
        extendedProps: {
          contentName: booking.contentData?.name,
          status: booking.status[0],
          bookingDuration: differenceInMinutes,
          adminNote: booking.adminNote,
          features: booking.features,
          featureDefinition: booking.contentData?.features,
          interactionId: booking.interactionId,
          username: booking.username,
          companyName,
          timeZone,
          count: booking.count,
          reservableName: reservable.name,
          tags: (reservable.tags ?? []).filter(
            (tag): tag is string => tag != null
          ),
        },
      });
    });
  });

  return events;
}
